import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  HStack,
  Link,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useParams } from "@tanstack/react-router"
import React from "react"
import { previewDocumentPdf } from "../../../../api/documentPdf"
import {
  type TransferDocumentMMWByIDDTO,
  TransferService,
} from "../../../../client"
import { useWarehouseInventoryBlocks } from "../../../../hooks/useWarehouseInventoryBlocks"
import DocumentPageHeader from "../../shared/DocumentPageHeader"
import DocumentProductCell from "../../shared/DocumentProductCell"
import { getDiscrepancyReasonLabel } from "../../shared/discrepancyReasons"
import { InventoryBlockReadNotice } from "../../shared/InventoryBlockReadNotice"
import PrintLanguageSelect from "../../shared/PrintLanguageSelect"
import TransferInfoPanel from "../../shared/TransferInfoPanel"
import { formatLotDate } from "../../shared/warehouseLots"

/** Renders quarantine and return locations with workflow-specific colors. */
function ReturnLocationLabel({ code }: { code?: string | null }) {
  const normalizedCode = code?.trim().toUpperCase()
  if (normalizedCode === "RETURN") {
    return <Badge colorScheme="orange">RETURN</Badge>
  }
  if (normalizedCode === "USZKODZONE") {
    return <Badge colorScheme="purple">USZKODZONE</Badge>
  }
  return <>{code || "—"}</>
}

export function DocumentMMRPage() {
  const params = useParams({ from: "/_layout/WMS/documents/doc-mmr/$docMmr" })
  const id = params.docMmr
  const toast = useToast()
  const qc = useQueryClient()
  const panelBg = useColorModeValue("white", "gray.800")
  const labelColor = useColorModeValue("gray.600", "gray.300")
  const [isSubmitting, setSubmitting] = React.useState(false)
  const [isConfirmOpen, setConfirmOpen] = React.useState(false)
  const cancelRef = React.useRef<HTMLButtonElement | null>(null)

  const { data, isPending, isError, error } =
    useQuery<TransferDocumentMMWByIDDTO>({
      queryKey: ["wms", "transfers", "mmr", "detail", id],
      queryFn: () => TransferService.getDocumentMMRByID(id),
    })
  const { blockedWarehouseIds, ready: inventoryBlocksReady } =
    useWarehouseInventoryBlocks()
  const inventoryBlockedForWarehouses =
    !inventoryBlocksReady ||
    !data?.from_warehouse?.id ||
    !data?.to_warehouse?.id ||
    blockedWarehouseIds.has(data.from_warehouse.id) ||
    blockedWarehouseIds.has(data.to_warehouse.id)
  const warnBlockedConfirmation = () => {
    toast({
      status: "warning",
      title: "Nie można potwierdzić dokumentu",
      description: inventoryBlocksReady
        ? "Potwierdzenie zablokowane: trwa inwentaryzacja lub nie udało się ustalić magazynu dokumentu."
        : "Sprawdzenie blokad magazynowych nadal trwa lub zakończyło się błędem. Ponów próbę po poprawnym odczycie.",
    })
  }

  const normalizeProduct = (p: any) => {
    if (!p) return undefined
    if (Array.isArray(p)) return p[0]
    return p
  }

  const fmtDate = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : "—"

  const onConfirm = async () => {
    if (inventoryBlockedForWarehouses) {
      warnBlockedConfirmation()
      return
    }
    try {
      setSubmitting(true)
      await TransferService.confirmDocumentMMRByID(id)
      toast({ status: "success", title: "Dokument MMR został potwierdzony" })
      await Promise.all([
        qc.invalidateQueries({
          queryKey: ["wms", "transfers", "mmr", "detail", id],
        }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmr"] }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmp"] }),
      ])
    } catch (e: any) {
      toast({
        status: "error",
        title: "Nie udało się potwierdzić MMR",
        description: e?.body?.error ?? e?.message ?? "Błąd",
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (isPending) {
    return (
      <Flex justify="center" align="center" w="full" h="60vh">
        <Spinner size="xl" color="main.accent" />
      </Flex>
    )
  }

  if (isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        Nie udało się załadować dokumentu MMR:{" "}
        {(error as any)?.message ?? "Błąd"}
      </Alert>
    )
  }

  if (!data) {
    return (
      <Flex justify="center" align="center" w="full" h="40vh">
        <Text color="gray.500">Nie znaleziono dokumentu</Text>
      </Flex>
    )
  }

  return (
    <Container maxW="9xl" py={6}>
      <DocumentPageHeader
        number={data.number}
        status={data.status}
        labelColor={labelColor}
        actions={
          <HStack>
            <PrintLanguageSelect
              onPrintPL={() =>
                previewDocumentPdf(
                  `/v1/transfers/MMR/${id}/pdf`,
                  `${data.number}.pdf`,
                  "pl",
                )
              }
              onPrintEN={() =>
                previewDocumentPdf(
                  `/v1/transfers/MMR/${id}/pdf`,
                  `${data.number}.pdf`,
                  "en",
                )
              }
            />
            {data.related_mmp && data.related_mmp_id ? (
              <Button
                as="a"
                size="sm"
                variant="outline"
                href={`/WMS/documents/doc-mmp/${data.related_mmp_id}`}
              >
                Otwórz MMP
              </Button>
            ) : null}
            {data.status === "draft" ? (
              <Button
                colorScheme="green"
                size="sm"
                onClick={() => {
                  if (inventoryBlockedForWarehouses) warnBlockedConfirmation()
                  else setConfirmOpen(true)
                }}
                isDisabled={inventoryBlockedForWarehouses}
                isLoading={isSubmitting}
              >
                Potwierdź MMR
              </Button>
            ) : null}
          </HStack>
        }
      />

      <InventoryBlockReadNotice />
      <Stack spacing={6}>
        <TransferInfoPanel
          bg={panelBg}
          summary={
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={4}
            >
              <GridItem>
                <Text fontSize="sm" color={labelColor}>
                  Z magazynu
                </Text>
                <Text>
                  {data.from_warehouse?.code} — {data.from_warehouse?.name}
                </Text>
              </GridItem>
              <GridItem>
                <Text fontSize="sm" color={labelColor}>
                  Na magazyn
                </Text>
                <Text>
                  {data.to_warehouse?.code} — {data.to_warehouse?.name}
                </Text>
              </GridItem>
              <GridItem>
                <Text fontSize="sm" color={labelColor}>
                  Powiązany MMP
                </Text>
                <Text>{data.related_mmp || "—"}</Text>
              </GridItem>
            </Grid>
          }
          details={
            <Stack spacing={2}>
              {data.related_mmp && data.related_mmp_id ? (
                <HStack>
                  <Text fontSize="sm" color={labelColor} minW="120px">
                    Powiązany MMP
                  </Text>
                  <Link
                    as="a"
                    color="green.600"
                    href={`/WMS/documents/doc-mmp/${data.related_mmp_id}`}
                  >
                    {data.related_mmp}
                  </Link>
                </HStack>
              ) : null}
            </Stack>
          }
          meta={
            <Stack spacing={1} color={labelColor} fontSize="sm">
              <Text>
                Utworzono: {fmtDate(data.created_at)} •{" "}
                {data.owner?.acronym ?? "—"}
              </Text>
              <Text>
                Potwierdzono: {fmtDate(data.confirmed_at)} •{" "}
                {data.confirmed_by?.acronym ?? "—"}
              </Text>
            </Stack>
          }
        />

        <Box bg={panelBg} borderWidth="1px" borderRadius="md" p={4}>
          <TableContainer>
            <Table size="sm" sx={{ tableLayout: "fixed" }} minW="1430px">
              <Thead>
                <Tr>
                  <Th w="52px">№</Th>
                  <Th w="300px">Produkt (SKU • Nazwa)</Th>
                  <Th w="180px">Partia</Th>
                  <Th w="180px">Miejsce oczekiwania</Th>
                  <Th w="180px">Miejsce zwrotu</Th>
                  <Th w="120px" isNumeric>
                    Ilość zwrotu
                  </Th>
                  <Th w="160px">Przyczyna</Th>
                  <Th w="260px">Komentarz</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data.items ?? []).map((item, index) => {
                  const prod = normalizeProduct(item.product)
                  return (
                    <Tr key={item.id}>
                      <Td>{index + 1}</Td>
                      <Td minW={0}>
                        <DocumentProductCell
                          sku={prod?.sku}
                          name={prod?.name}
                        />
                      </Td>
                      <Td>
                        <Stack spacing={0.5}>
                          <Text fontSize="sm" color="gray.700">
                            {item.lot_no || "—"}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {item.production_date
                              ? `Prod. ${formatLotDate(item.production_date)}`
                              : "Prod.: —"}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={
                              item.expiration_date ? "orange.600" : "gray.500"
                            }
                          >
                            {item.expiration_date
                              ? `Ważne do ${formatLotDate(item.expiration_date)}`
                              : "Termin: —"}
                          </Text>
                        </Stack>
                      </Td>
                      <Td>
                        <ReturnLocationLabel code={item.location_code} />
                      </Td>
                      <Td>
                        <ReturnLocationLabel code={item.to_location_code} />
                      </Td>
                      <Td isNumeric>{item.rejected_qty || item.qty}</Td>
                      <Td>
                        {getDiscrepancyReasonLabel(item.discrepancy_reason)}
                      </Td>
                      <Td>{item.discrepancy_comment || "—"}</Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmOpen(false)}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>Potwierdzić dokument MMR?</AlertDialogHeader>
          <AlertDialogBody>
            Czy na pewno chcesz potwierdzić ten dokument MMR? Operacja zwróci
            odrzucony lub uszkodzony towar na magazyn źródłowy.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef as any}
              onClick={() => setConfirmOpen(false)}
              variant="ghost"
            >
              Anuluj
            </Button>
            <Button
              colorScheme="green"
              ml={3}
              onClick={async () => {
                await onConfirm()
                setConfirmOpen(false)
              }}
              isLoading={isSubmitting}
              isDisabled={inventoryBlockedForWarehouses}
            >
              Potwierdź
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Container>
  )
}
